import React, { useCallback, useEffect, useRef } from 'react';

import { HeartOutlineIcon } from '../assets/icons/HeartOutlineIcon';
import { useTranslation } from '../contexts/TranslationContext';
import './ExpertFollowButton.scss';

const PARTICLE_ANIMATION_MS = 1650;

export interface ExpertFollowButtonProps {
  isFollowing: boolean;
  followersCount: number;
  disabled?: boolean;
  onClick: () => void;
}

const ExpertFollowButton: React.FC<ExpertFollowButtonProps> = ({
  isFollowing,
  followersCount,
  disabled = false,
  onClick,
}) => {
  const { t } = useTranslation();
  const particlesRef = useRef<HTMLDivElement>(null);
  const clearTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (clearTimerRef.current) {
        window.clearTimeout(clearTimerRef.current);
      }
    };
  }, []);

  const triggerParticles = useCallback(() => {
    const el = particlesRef.current;
    if (!el) return;
    el.classList.remove('expert-follow-button__particles--animated');
    void el.offsetWidth;
    el.classList.add('expert-follow-button__particles--animated');
    if (clearTimerRef.current) {
      window.clearTimeout(clearTimerRef.current);
    }
    clearTimerRef.current = window.setTimeout(() => {
      el.classList.remove('expert-follow-button__particles--animated');
      clearTimerRef.current = undefined;
    }, PARTICLE_ANIMATION_MS);
  }, []);

  const handleClick = () => {
    if (disabled) return;
    if (!isFollowing) {
      triggerParticles();
    }
    onClick();
  };

  const countStr = Math.max(0, followersCount).toLocaleString();

  return (
    <div className="expert-follow-button__wrap">
      <button
        type="button"
        className={`expert-follow-button__btn${isFollowing ? ' expert-follow-button__btn--following' : ''}`}
        onClick={handleClick}
        disabled={disabled}
      >
        <span className="expert-follow-button__icon" aria-hidden>
          <HeartOutlineIcon width={20} height={20} />
        </span>
        <span className="expert-follow-button__text">
          <span className="expert-follow-button__label">
            {isFollowing ? t.common.following : t.common.follow}
          </span>
          <span className="expert-follow-button__sep" aria-hidden>
            ·
          </span>
          <span className="expert-follow-button__count">{countStr}</span>
        </span>
      </button>
      <div
        ref={particlesRef}
        className="expert-follow-button__particles"
        aria-hidden
      />
    </div>
  );
};

export default ExpertFollowButton;
